import * as d3 from 'd3';
import { versionColor } from '../colors.js';
import { fmtBytes } from '../eventLog.js';

// Nodes = asset versions; if a version is currently a live dictionary → double border.
// Edges = delta artifacts (labeled Δvi→vj), colored with delta gradient.
// Layout: rows per asset (top-down), most recent versions on the right.
// Per-node sparklines drawn on shared canvas.

export function createAssetGraph(container, svgEl, canvasEl) {
  const svg = d3.select(svgEl);
  const gRoot = svg.append('g');
  const gEdges = gRoot.append('g').attr('class', 'edges');
  const gNodes = gRoot.append('g').attr('class', 'nodes');
  const ctx = canvasEl.getContext('2d');
  let transform = d3.zoomIdentity;
  let windowN = 12;
  const zoom = d3.zoom().scaleExtent([0.3, 2.5]).on('zoom', (ev) => {
    transform = ev.transform;
    gRoot.attr('transform', transform);
    drawSparklines(lastSnap);
  });
  svg.call(zoom);

  let lastSnap = null;

  function setWindow(n) {
    windowN = Math.max(2, Math.min(200, n | 0 || 12));
    if (lastSnap) update(lastSnap);
  }

  function update(snap) {
    lastSnap = snap;
    const bounds = container.getBoundingClientRect();
    const W = bounds.width, H = bounds.height;
    svg.attr('viewBox', `0 0 ${W} ${H}`);
    const dpr = window.devicePixelRatio || 1;
    canvasEl.width = W * dpr; canvasEl.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    // Layout params. Row height is computed later, once we know how tall the
    // stack of Δ badges gets (see level assignment below).
    const nodeW = 44, nodeH = 30;
    const gap = 44;          // horizontal room between versions so arcs and Δ badges stay readable
    const sparkH = 24;       // canvas sparkline strip drawn under every node
    const rowPad = 16;       // breathing room under the sparkline
    const rightPad = 20;
    const leftPad = 90; // room for asset label

    // Build nodes: last N versions per asset.
    const nodes = [];
    const dictSet = new Set();
    for (const d of snap.dictionaries) dictSet.add(`${d.sourceAssetId}@${d.sourceVersion}`);
    snap.assets.forEach((a, ai) => {
      const versions = snap.versions.filter((v) => v.assetId === a.id).sort((x, y) => x.version - y.version);
      const slice = versions.slice(-windowN);
      slice.forEach((v, i) => {
        nodes.push({
          key: `${a.id}@${v.version}`,
          assetId: a.id,
          version: v.version,
          isDict: dictSet.has(`${a.id}@${v.version}`),
          hitsRate: (a.requestsServed / Math.max(1, snap.t)) * (1 - Math.abs(i - (slice.length - 1)) / slice.length),
          x: leftPad + (slice.length - 1 - i) * (nodeW + gap) + 0, // newer on right; we lay left-to-right by version ascending
          _laneIndex: i,
          _laneCount: slice.length,
          _rowIndex: ai,
          // y assigned after badge levels are known
        });
      });
      // Re-adjust x now that we know lane count.
      const laneNodes = nodes.filter((n) => n.assetId === a.id);
      const rowWidth = laneNodes.length * (nodeW + gap) - gap;
      laneNodes.forEach((n, i) => { n.x = leftPad + i * (nodeW + gap); n.width = nodeW; n.height = nodeH; });
    });

    // Edges: deltas whose both endpoints appear in nodes.
    const nodeKey = (aid, v) => `${aid}@${v}`;
    const nodeMap = new Map(nodes.map((n) => [n.key, n]));
    const edges = [];
    for (const d of snap.deltas) {
      const from = nodeMap.get(nodeKey(d.assetId, d.fromVersion));
      const to = nodeMap.get(nodeKey(d.assetId, d.toVersion));
      if (from && to) edges.push({ key: d.id, from, to, delta: d });
    }

    // Stack colliding Δ badges into levels: within an asset row, an edge whose
    // badge would horizontally overlap an already-placed one (e.g. Δv1→v3 over
    // Δv1→v2 and Δv2→v3) takes the lowest free level above it. Shorter spans
    // are placed first so they stay close to the nodes.
    const BADGE_W = 68, BADGE_H = 24;
    const METER_H = 16; // hit meter + gap drawn above each badge on the canvas
    const LEVEL_STEP = BADGE_H + METER_H + 6;
    const byAsset = d3.group(edges, (e) => e.delta.assetId);
    for (const group of byAsset.values()) {
      const placed = [];
      const sorted = [...group].sort((a, b) =>
        Math.abs(a.to._laneIndex - a.from._laneIndex) - Math.abs(b.to._laneIndex - b.from._laneIndex) ||
        a.from._laneIndex - b.from._laneIndex);
      for (const e of sorted) {
        e.midX = (e.from.x + e.from.width + e.to.x) / 2;
        let level = 0;
        while (placed.some((p) => p.level === level && Math.abs(p.midX - e.midX) < BADGE_W + 8)) level++;
        e.level = level;
        placed.push(e);
      }
    }
    const maxLevel = edges.reduce((m, e) => Math.max(m, e.level), 0);

    // Vertical layout, now that we know how tall the badge stack is.
    const arcHeadroom = 56 + LEVEL_STEP * maxLevel; // room above nodes for arcs + stacked badges
    const minRowH = arcHeadroom + nodeH + sparkH + rowPad;
    const rowH = Math.max(minRowH, Math.min(minRowH + 60, (H - 20) / Math.max(1, snap.assets.length)));
    // Nodes sit at the bottom of their row; the space above them holds the arcs.
    const nodeY = (rowIndex) => 12 + rowIndex * rowH + (rowH - rowPad - sparkH - nodeH);
    for (const n of nodes) n.y = nodeY(n._rowIndex);

    // Delta gradient defs (one per edge) so we can fill the arc badge with a
    // start-color → end-color gradient matching the two versions.
    const defs = svg.selectAll('defs').data([0]).join('defs');
    const gradSel = defs.selectAll('linearGradient.d-arc').data(edges, (d) => d.key);
    const gradEnter = gradSel.enter().append('linearGradient').attr('class', 'd-arc')
      .attr('id', (d) => 'garc_' + d.key)
      .attr('x1', 0).attr('x2', 1).attr('y1', 0).attr('y2', 0);
    gradEnter.append('stop').attr('offset', '0%');
    gradEnter.append('stop').attr('offset', '100%');
    const gradMerged = gradSel.merge(gradEnter);
    gradMerged.select('stop:nth-child(1)').attr('stop-color', (d) => versionColor(d.delta.assetId, d.delta.fromVersion));
    gradMerged.select('stop:nth-child(2)').attr('stop-color', (d) => versionColor(d.delta.assetId, d.delta.toVersion));
    gradSel.exit().remove();

    // Draw edges (SVG paths + delta-badge in the middle).
    const eSel = gEdges.selectAll('g.e').data(edges, (d) => d.key);
    const eEnter = eSel.enter().append('g').attr('class', 'e');
    eEnter.append('path').attr('class', 'arc').attr('fill', 'none').attr('stroke-width', 1.5);
    const badge = eEnter.append('g').attr('class', 'badge');
    badge.append('rect').attr('class', 'bg').attr('rx', 3);
    badge.append('text').attr('class', 'lbl').attr('font-size', 9).attr('text-anchor', 'middle').attr('font-weight', 600).attr('fill', '#0e1116');
    badge.append('text').attr('class', 'meta').attr('font-size', 8).attr('text-anchor', 'middle').attr('fill', '#8b949e');
    const eMerged = eSel.merge(eEnter);

    const deltaBadges = []; // collected for canvas hit meters drawn above each badge
    eMerged.each(function (d) {
      const g = d3.select(this);
      const x1 = d.from.x + d.from.width, y1 = d.from.y + d.from.height / 2;
      const x2 = d.to.x, y2 = d.to.y + d.to.height / 2;
      const midX = (x1 + x2) / 2;
      // Badge center comes from the collision level; the arc is then bent so
      // its midpoint passes through the badge. For Q(t) at t=0.5,
      // y = 0.25*y1 + 0.5*apexY + 0.25*y2, so apexY = 2*by - (y1+y2)/2.
      const bx = midX;
      const by = Math.min(y1, y2) - 34 - (d.level ?? 0) * LEVEL_STEP;
      const apexY = 2 * by - (y1 + y2) / 2;
      g.select('path.arc')
        .attr('d', `M${x1},${y1} Q${midX},${apexY} ${x2},${y2}`)
        .attr('stroke', versionColor(d.delta.assetId, d.delta.toVersion))
        .attr('opacity', 0.6);
      const bg = g.select('g.badge').attr('transform', `translate(${bx - BADGE_W / 2}, ${by - BADGE_H / 2})`);
      bg.select('rect.bg')
        .attr('width', BADGE_W).attr('height', BADGE_H)
        .attr('fill', `url(#garc_${d.key})`)
        .attr('stroke', 'rgba(0,0,0,0.35)').attr('stroke-width', 1);
      bg.select('text.lbl')
        .attr('x', BADGE_W / 2).attr('y', 11)
        .text(`Δv${d.delta.fromVersion}→v${d.delta.toVersion}`);
      bg.select('text.meta')
        .attr('x', BADGE_W / 2).attr('y', 20)
        .text(fmtBytes(d.delta.sizeBytes));
      deltaBadges.push({ x: bx - BADGE_W / 2, y: by - BADGE_H / 2, w: BADGE_W, delta: d.delta });
      bg.append('title'); // ensure title exists
      bg.select('title').text(
        `Δ ${d.delta.assetId} v${d.delta.fromVersion} → v${d.delta.toVersion}\n` +
        `size ${d.delta.sizeBytes} B\nhits ${d.delta.hits ?? 0}\ncreated t=${d.delta.createdAt.toFixed(1)}s`
      );
    });
    eSel.exit().remove();

    // Row labels (asset name).
    const labels = gNodes.selectAll('text.rowlbl').data(snap.assets, (a) => a.id);
    labels.enter().append('text').attr('class', 'rowlbl').attr('font-size', 11).attr('fill', '#8b949e')
      .merge(labels)
      .attr('x', 8)
      .attr('y', (_, i) => nodeY(i) + nodeH / 2 + 4)
      .text((a) => `${a.name} @v${a.currentVersion}`);
    labels.exit().remove();

    // Nodes (rects + labels).
    const nSel = gNodes.selectAll('g.n').data(nodes, (n) => n.key);
    const nEnter = nSel.enter().append('g').attr('class', 'n');
    nEnter.append('rect').attr('rx', 3);
    nEnter.append('rect').attr('class', 'inner').attr('rx', 2).attr('fill', 'none');
    nEnter.append('text').attr('class', 'lbl').attr('font-size', 10).attr('text-anchor', 'middle').attr('fill', '#0e1116').attr('font-weight', 600);
    const nMerged = nSel.merge(nEnter);
    nMerged.attr('transform', (n) => `translate(${n.x}, ${n.y})`);
    nMerged.select('rect').attr('width', (n) => n.width).attr('height', (n) => n.height)
      .attr('fill', (n) => versionColor(n.assetId, n.version))
      .attr('stroke', (n) => n.isDict ? '#f0b429' : '#2a313c')
      .attr('stroke-width', (n) => n.isDict ? 2 : 1);
    nMerged.select('rect.inner').attr('x', 3).attr('y', 3).attr('width', (n) => n.width - 6).attr('height', (n) => n.height - 6)
      .attr('stroke', (n) => n.isDict ? '#f0b429' : 'none').attr('stroke-width', (n) => n.isDict ? 1.5 : 0);
    nMerged.select('text.lbl').attr('x', (n) => n.width / 2).attr('y', 14).text((n) => {
      const short = n.assetId + 'v' + n.version;
      return short;
    });
    nSel.exit().remove();

    // Sparklines on shared canvas.
    drawSparklines(snap, nodes, rowH, deltaBadges);
    lastSnap._nodes = nodes;
    lastSnap._rowH = rowH;
    lastSnap._badges = deltaBadges;
  }

  function drawSparklines(snap, nodes, rowH, badges) {
    if (!snap) return;
    const list = nodes ?? snap._nodes ?? [];
    const blist = badges ?? snap._badges ?? [];
    if (!list.length) return;
    const rh = rowH ?? snap._rowH ?? 80;
    // Clear the full backing store (in device pixels) before applying the zoom transform;
    // otherwise pan/zoom leaves ghost strokes from previous frames.
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
    ctx.restore();
    ctx.save();
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.translate(transform.x, transform.y);
    ctx.scale(transform.k, transform.k);
    // For each node, draw a mini bar under the node representing request-rate approx.
    // Data: use snap.stats.timeSeries filtered to that asset (approximated: bucket bytesServed).
    const ts = snap.stats.timeSeries.slice(-40);
    for (const n of list) {
      const x = n.x, y = n.y + n.height + 4;
      const w = n.width, h = 14;
      ctx.fillStyle = 'rgba(255,255,255,0.05)';
      ctx.fillRect(x, y, w, h);
      // rate proxy: (n.hitsRate)
      const rate = Math.min(1, n.hitsRate / 5);
      ctx.fillStyle = versionColor(n.assetId, n.version);
      ctx.globalAlpha = 0.7;
      ctx.fillRect(x, y + h * (1 - rate), w, h * rate);
      ctx.globalAlpha = 1;
      // "presence in storage" strip below
      ctx.strokeStyle = n.isDict ? '#f0b429' : (n.assetId ? '#4ea1ff' : '#2a313c');
      ctx.strokeRect(x + 0.5, y + h + 2 + 0.5, w - 1, 3);
    }
    // Hit meters above each Δ badge, mirroring the per-node bars.
    // Saturating scale on cumulative hits (h/(h+6)): every hit visibly grows
    // the bar, with diminishing steps — never pinned at 0% or 100%.
    for (const b of blist) {
      const h = 14;
      const x = b.x, y = b.y - 2 - h, w = b.w;
      ctx.fillStyle = 'rgba(255,255,255,0.05)';
      ctx.fillRect(x, y, w, h);
      const hits = b.delta.hits ?? 0;
      const rate = hits / (hits + 6);
      ctx.fillStyle = versionColor(b.delta.assetId, b.delta.toVersion);
      ctx.globalAlpha = 0.7;
      ctx.fillRect(x, y + h * (1 - rate), w, h * rate);
      ctx.globalAlpha = 1;
    }
    ctx.restore();
  }

  return { update, setWindow };
}
